import { useMutation } from '@connectrpc/connect-query'
import { deleteSingularDay } from '@ebalance/proto/bffuser/v1/singular_day_service-SingularDayService_connectquery'
import type { ListSingularDaysResponse } from '@ebalance/proto/bffuser/v1/singular_day_service_pb'
import { useQueryClient } from '@tanstack/react-query'

import { type InvalidatingMutationResultFor } from '~/hooks/useInvalidatingMutation'
import { singularDayQueryKeys } from '~/pages/AdminSingularDay/hooks/queryKeys'

/**
 * 特異日削除 (論理削除) mutation hook (AC-07)。
 *
 * identity は surrogate `id` (UUID)。 論理削除された行は一覧 (有効行のみ) から
 * 消えるだけなので、 削除後の一覧は「同一 id の行を除外する」 だけで確定できる。
 * date は同一日付に複数行が併存しうるため identity には使えない。 そのため
 * invalidate + 再取得ではなく `setQueriesData` で cache から当該行を権威的に
 * 除去する。 optimistic locking: expected_updated_at を request に含める。
 */
export function useDeleteSingularDay(): InvalidatingMutationResultFor<
  typeof deleteSingularDay
> {
  const queryClient = useQueryClient()
  return useMutation(deleteSingularDay, {
    onSuccess: (_data, variables) => {
      queryClient.setQueriesData<ListSingularDaysResponse>(
        { queryKey: singularDayQueryKeys.list },
        (old) => {
          if (old === undefined) {
            return old
          }
          return {
            ...old,
            singularDays: old.singularDays.filter(
              (sd) => sd.id !== variables.id,
            ),
          }
        },
      )
    },
  })
}
